/**
 * Single source of truth for "may this user see wallet UI?".
 *
 * `staff.has_wallet` is the backend flag documented for exactly this purpose
 * (see `.agents/docs/API.md` — Wallet). It is per-company, so it already covers
 * the "only some users for now" part of the rollout.
 *
 * `WALLET_REQUIRES_PRO` is the phase-1 restriction: the wallet ships to PRO
 * only. Releasing it to the remaining profiles is a change *here* (flip the
 * flag) and nowhere else — every wallet component reads this helper.
 */
export const WALLET_REQUIRES_PRO = true;

/**
 * Tenant that must **never** see wallet UI, whatever the account says: Service
 * Box (`5`). It is a storefront-level ban, not a profile rule — a Service Box
 * user with `has_wallet` and `is_pro` still gets nothing.
 */
export const WALLET_BANNED_TENANT = '5';

/**
 * Whether this storefront has a wallet at all. Every wallet surface goes through
 * this (directly, or through `canAccessWallet`), so the ban cannot be defeated by
 * a rollout flag or by data arriving in a payload — including the accumulation
 * tags, which deliberately do not require a wallet during the rollout.
 */
export const isWalletTenant = (): boolean =>
  process.env.NEXT_PUBLIC_TENANT !== WALLET_BANNED_TENANT;

const canAccessWallet = (user?: TLoggedUser | null): boolean => {
  if (!isWalletTenant()) {
    return false;
  }

  if (!user?.staff?.has_wallet) {
    return false;
  }

  if (WALLET_REQUIRES_PRO && !user.staff.is_pro) {
    return false;
  }

  return true;
};

export default canAccessWallet;
